/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Model Image Studio Aspect Ratio Selector (Task 10.4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { useImageStudioStore, ASPECT_RATIOS } from '../../stores/useImageStudioStore';
import { useI18nStore } from '../../stores/useI18nStore';
import { Layout } from 'lucide-react';

export const AspectRatioSelector: React.FC = () => {
  const { t } = useI18nStore();
  const { aspectRatio, setAspectRatio } = useImageStudioStore();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-heading font-bold text-foreground flex items-center gap-1.5">
          <Layout className="w-4 h-4 text-primary" />
          <span>{t('media.selectRatioTitle')}</span>
        </label>
        <span className="text-[11px] text-foreground-muted font-mono">
          {ASPECT_RATIOS.find((r) => r.id === aspectRatio)?.width} x{' '}
          {ASPECT_RATIOS.find((r) => r.id === aspectRatio)?.height} px
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
        {ASPECT_RATIOS.map((item) => {
          const isSelected = aspectRatio === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setAspectRatio(item.id)}
              className={`p-3 rounded-2xl border text-center flex flex-col items-center justify-between gap-2.5 transition-all duration-200 ${
                isSelected
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/40 shadow-glow-primary/20'
                  : 'border-surface-border bg-surface hover:bg-surface-elevated'
              }`}
            >
              {/* Aspect Ratio Shape Preview */}
              <div className="w-12 h-10 flex items-center justify-center">
                <div
                  className={`border-2 rounded transition-colors ${
                    isSelected ? 'border-primary bg-primary/20' : 'border-foreground-muted/60 bg-muted/30'
                  }`}
                  style={{
                    width:
                      item.id === '1:1'
                        ? '28px'
                        : item.id === '16:9'
                        ? '38px'
                        : item.id === '9:16'
                        ? '20px'
                        : item.id === '4:3'
                        ? '32px'
                        : '42px',
                    height:
                      item.id === '1:1'
                        ? '28px'
                        : item.id === '16:9'
                        ? '22px'
                        : item.id === '9:16'
                        ? '36px'
                        : item.id === '4:3'
                        ? '24px'
                        : '18px',
                  }}
                />
              </div>

              <div className="space-y-0.5">
                <div className="text-xs font-heading font-extrabold text-foreground">
                  {item.label}
                </div>
                <p className="text-[10px] text-foreground-muted truncate max-w-[100px]">
                  {item.subLabel}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
